import { useEffect, useState, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { UserMobileNumberDetailResponse } from "../../deposit/models/response";
import { BLOB_IMAGE_URL } from "../../../config/appConfig";
import { BlobContainerConsts } from "../../../models/constants/blobContainerConsts";
import { WithdrawType, WithdrawTypeListResponseList } from "../../withdraw/models";
import CreateWithdrawRequest from "./CreateWithdrawRequest";
import { Button, Card, CardBody } from "../../../components/ui";
import localization from "../../../config/localization";
import depositv1Service from "../../deposit/services/deposit.service";
import { localStorageService } from "../../../services/localStorageService";
import CreateABCCryptoWithdrawRequest from "./CreateABCCryptoWithdrawRequest";
import { useAppDispatch } from "../../../store/hooks";
import { changeLoaderState } from "../../../reducers/appStateReducer";
import { DeviceType } from "../../../models/constants/deviceType";
import userDeviceStore from "../../../store/device.store";
import { Modal } from "../../../components/ui/modals";

interface WithdrawV1Props {
  withdrawFlow: string;
  withdrawTypes: WithdrawTypeListResponseList[];
  username: string | null;
}

export default function WithdrawV1(
  { withdrawFlow,
    withdrawTypes,
    username }: WithdrawV1Props) {
  const navigate = useNavigate();
  const [selectedWithdrawType, setSelectedWithdrawType] =
    useState<WithdrawTypeListResponseList | null>(null);
  const [userMobileDetail, setUserMobileDetail] =
    useState<UserMobileNumberDetailResponse | null>(null);
  const [showVerifyMobileModal, setShowVerifyMobileModal] =
    useState<boolean>(false);
  const dispatch = useAppDispatch();
  const deviceType = userDeviceStore((state) => state.deviceType);

  const usernameRef = useRef<string | null>("User");

  useEffect(() => {
    dispatch(changeLoaderState(true));
    init();
  }, []);

  const init = async () => {
    const response = await depositv1Service.getUserMobileDetail();
    if (response) {
      setUserMobileDetail(response);
      if (!userMobileDetail?.ismobileverified) {
        usernameRef.current = localStorageService.getItem<string>("username");
      }
    }
    dispatch(changeLoaderState(false));
  };

  const onWithdrawTypeClick = async (item: WithdrawTypeListResponseList) => {
    if (
      item.name?.toLowerCase() === WithdrawType.ABCCrypto &&
      !userMobileDetail?.ismobileverified
    ) {
      setShowVerifyMobileModal(true);
      return;
    }
    setSelectedWithdrawType(item);
  };

  const handleVerifyProceed = () => {
    navigate("/profile");
  };

  if (selectedWithdrawType) {
    if (selectedWithdrawType.name?.toLowerCase() === WithdrawType.ABCCrypto) {
      return (
        <CreateABCCryptoWithdrawRequest
          selectedWithdrawType={selectedWithdrawType}
          withdrawFlow={withdrawFlow}
          onBack={() => setSelectedWithdrawType(null)}
        />
      );
    }
    return (
      <CreateWithdrawRequest
        selectedWithdrawType={selectedWithdrawType}
        withdrawFlow={withdrawFlow}
        username={username}
        onBack={() => setSelectedWithdrawType(null)}
      />
    );
  }

  const content = (
    <div className="p-4">

      <div className="grid grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-5">
        {withdrawTypes.map((item) => (
          <div key={item.id} className="relative rounded-2xl bg-black-gradient border-2 border-[#2C2C2C] py-3 flex flex-col items-center text-white">

            <div className="flex items-center justify-center h-20">
              <img
                src={item.logoUrl ? `${BLOB_IMAGE_URL}${BlobContainerConsts.WithdrawType}/${item.logoUrl}` : "/img/photos/icon-upload.png"}
                alt={item.name}
                className="w-[80px] h-[50px] object-contain"
              />
            </div>

            <p className="mt-2 text-center text-sm font-medium leading-[22px] line-clamp-2">
              {item.accountName}
            </p>

            <div className="mt-3 w-full bg-black border-y border-[#3B3B3B] py-3 text-center text-xs text-gray-300">
              <span className="font-normal mr-1">{localization.min_text}</span>
              <span className="font-semibold text-gray-200">
                {"\u20B9"}{item.minimumAmount}
              </span>

              <span className="mx-2 font-normal">-</span>

              <span className="font-normal mr-1">{localization.max_text}</span>
              <span className="font-semibold text-gray-200">
                {"\u20B9"}{item.maximumAmount}
              </span>
            </div>

            <Button onClick={() => onWithdrawTypeClick(item)} className="bg-secondary-gradient rounded-md text-white text-xs leading-[14px] font-medium py-2 px-5 mt-4 cursor-pointer">
              {localization.select_btn}
            </Button>

          </div>
        ))}
      </div>

      {
        showVerifyMobileModal && (
          <Modal isOpen={showVerifyMobileModal} onClose={handleVerifyProceed}>
            <p className="mt-3 text-[18px] leading-[28px] text-gray-600">
              <span className="mr-2">{localization.hi_lbl}</span>
              <span className="font-bold text-black">{usernameRef.current}</span>
            </p>

            <p className="mt-2 px-4 text-[18px] font-semibold leading-[28px] text-gray-600">
              {localization.withdraw_mobile_verify_note}
            </p>

            <Button
              onClick={handleVerifyProceed}
              className="mt-6 w-full rounded-lg bg-[#111111] py-3.5 font-semibold text-white hover:bg-[#EDEAEA] hover:text-black transition-all duration-200"
            >
              {localization.proceed}
            </Button>
          </Modal>
        )
      }
    </div >
  );

  return deviceType !== DeviceType.Web ? (
    content
  ) : (
    <Card>
      <CardBody showGradient>
        <div className="w-full max-w-4xl mx-auto">
          {content}
        </div>
      </CardBody>
    </Card>
  );
}
